'use client';
import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, Loader2, Sparkles } from 'lucide-react';

interface DisplayMessage {
  id: string;
  role: 'assistant' | 'user';
  content: string;
}

const quickReplies = ['Pricing info', 'Book an event', 'Wedding packages', 'Vendor services', 'Contact info'];

const SYSTEM_PROMPT = `You are Max, the AI assistant for Maximum Effort Mobile DJ & Karaoke Services, owned by Christopher Harris (DJ ExXtra) in Northwest Indiana.

Key facts:
- Standard events (karaoke, bingo, trivia, bars, parties): $75/hr, $100 retainer
- Wedding packages: $150–$200/hr, $200 retainer  
- All retainers are non-refundable upon cancellation
- Weddings: 60-day cancellation notice; standard events: 30-day notice
- Payment methods: Venmo, PayPal, Cash App, Zelle, Chime, or card via Stripe
- Balance due 45 days before event
- Services: DJ, karaoke, bingo, trivia, private parties, corporate events, bar events, graduations, weddings
- Vendor coordination: photographers, videographers, bartenders, caterers, limos, security, photo booths, florists, bakers, decorators, hair & makeup
- Contact: 219-614-9534 | maximumeffortmdj@gmail.com
- Christopher can also officiate wedding ceremonies
- To book or inquire, scroll down to the "Book Your Event" section on this page or call/text 219-614-9534

Booking FAQ:
- Q: How do I book? A: Fill out the inquiry form on this page (scroll to "Book Your Event") or call/text 219-614-9534. We'll send a proposal within 24 hours. - Q: What's the deposit? A: A non-refundable retainer secures your date — $100 for standard events, $200 for weddings.
- Q: When is the balance due? A: 45 days before your event.
- Q: Do you travel? A: Yes, we serve all of Northwest Indiana and surrounding areas.
- Q: Can I customize my playlist? A: Absolutely! Clients get access to a Spotify playlist builder in their portal.
- Q: What if I need to cancel? A: Standard events require 30-day notice; weddings require 60-day notice. Retainers are non-refundable.

When prospects ask about booking, always guide them to the inquiry form on this page. Be friendly, concise, and helpful. Keep responses under 3 sentences when possible.`;

export default function AIAssistantWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [displayMessages, setDisplayMessages] = useState<DisplayMessage[]>([
    {
      id: 'msg-welcome',
      role: 'assistant',
      content: "Hey! I'm Max, your Maximum Effort AI assistant. I can answer questions about pricing, booking, vendors, and more. How can I help you today?",
    },
  ]);
  const [conversationHistory, setConversationHistory] = useState<Array<{ role: string; content: string }>>([]);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [displayMessages, open]);

  const handleSend = async (text: string) => {
    if (!text.trim() || isLoading) return;

    const userMsg: DisplayMessage = { id: `msg-user-${Date.now()}`, role: 'user', content: text };
    setDisplayMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    const newHistory = [...conversationHistory, { role: 'user', content: text }];
    setConversationHistory(newHistory);

    try {
      const res = await fetch('/api/ai/chat-completion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: 'OPEN_AI',
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: SYSTEM_PROMPT },
            ...newHistory,
          ],
          stream: false,
          parameters: {
            max_completion_tokens: 300,
          },
        }),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(data.error || `Request failed: ${res.status}`);
      }

      const aiContent = data?.choices?.[0]?.message?.content || 'Sorry, I could not generate a response.';
      const aiMsg: DisplayMessage = {
        id: `msg-ai-${Date.now()}`,
        role: 'assistant',
        content: aiContent,
      };
      setDisplayMessages(prev => [...prev, aiMsg]);
      setConversationHistory(prev => [...prev, { role: 'assistant', content: aiContent }]);
    } catch (err) {
      const errorMsg: DisplayMessage = {
        id: `msg-err-${Date.now()}`,
        role: 'assistant',
        content: err instanceof Error ? `Error: ${err.message}` : 'Sorry, I ran into an issue. Please try again in a moment.',
      };
      setDisplayMessages(prev => [...prev, errorMsg]);
      console.error('Max assistant error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-4 right-4 z-50 w-14 h-14 rounded-full gold-gradient flex items-center justify-center glow-gold hover:opacity-90 active:scale-95 transition-all duration-150 shadow-2xl"
        aria-label="Open AI assistant"
      >
        {open ? <X size={22} className="text-black" /> : <MessageCircle size={22} className="text-black" />}
      </button>

      {/* Chat window */}
      {open && (
        <div className="fixed bottom-20 right-4 z-50 w-80 sm:w-96 rounded-2xl card-glass neon-border-gold shadow-2xl flex flex-col overflow-hidden scale-in">
          {/* Header */}
          <div className="px-4 py-3 border-b border-border flex items-center gap-3 gold-gradient">
            <div className="w-8 h-8 rounded-full bg-black/20 flex items-center justify-center">
              <Sparkles size={16} className="text-black" />
            </div>
            <div>
              <p className="text-sm font-bold text-black">Max — AI Assistant</p>
              <p className="text-xs text-black/70">Powered by OpenAI · Maximum Effort MDJ</p>
            </div>
            <div className="ml-auto flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs text-black/70">Online 24/7</span>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-72 scrollbar-dark">
            {displayMessages.map((msg) => (
              <div key={msg.id} className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${msg.role === 'assistant' ? 'bg-primary/20 border border-primary/40' : 'bg-accent/20 border border-accent/40'}`}>
                  {msg.role === 'assistant' ? <Bot size={12} className="text-primary" /> : <User size={12} className="text-accent" />}
                </div>
                <div className={`max-w-[75%] px-3 py-2 rounded-xl text-sm leading-relaxed ${msg.role === 'assistant' ? 'bg-muted text-foreground rounded-tl-none' : 'bg-primary/20 border border-primary/30 text-foreground rounded-tr-none'}`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex gap-2">
                <div className="w-7 h-7 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center">
                  <Bot size={12} className="text-primary" />
                </div>
                <div className="px-3 py-2 rounded-xl bg-muted rounded-tl-none">
                  <Loader2 size={14} className="text-muted-foreground animate-spin" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick replies */}
          <div className="px-3 py-2 border-t border-border flex gap-1.5 overflow-x-auto scrollbar-dark">
            {quickReplies.map(qr => (
              <button
                key={`qr-${qr}`}
                onClick={() => handleSend(qr)}
                className="shrink-0 px-2.5 py-1 rounded-full text-xs font-medium border border-primary/30 text-primary hover:bg-primary/10 transition-colors"
              >
                {qr}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-border flex gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(input); } }}
              placeholder="Ask me anything..."
              className="flex-1 px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors"
            />
            <button
              onClick={() => handleSend(input)}
              disabled={isLoading || !input.trim()}
              className="w-9 h-9 rounded-lg gold-gradient flex items-center justify-center hover:opacity-90 active:scale-95 transition-all disabled:opacity-40"
              aria-label="Send message"
            >
              <Send size={14} className="text-black" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}